import React, { useRef, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import { Box, ButtonBase, Typography } from '@mui/material';

import { AutoCompleteSelectUI, FormErrorUI, FormLabelUI, InputUI, RichTextEditorUI } from '../Interface';
import { TemplateDocumentLevel, TemplateType } from '../../config/template';
import { worksheetEmailTemplateVariables } from '../../config/worksheetEmail';
import SmsTemplateVariablePanel from './SmsTemplateVariablePanel';

const templateTypeOptions = [
  { value: TemplateType.EMAIL, label: 'E-mail' },
  { value: TemplateType.SMS, label: 'SMS' },
  { value: TemplateType.TEXT, label: 'Szöveg' },
];

const documentLevelOptions = [
  { value: TemplateDocumentLevel.HEADER, label: 'Fej' },
  { value: TemplateDocumentLevel.LINE, label: 'Tétel' },
];

const documentEmailTemplateVariables = [
  { token: '{{docNum}}', description: 'Bizonylatszám' },
  { token: '{{companyName}}', description: 'Partner neve' },
  { token: '{{DocDate}}', description: 'Dátum' },
  { token: '{{issueDate}}', description: 'Bizonylat dátuma' },
  { token: '{{dueDate}}', description: 'Esedékesség dátuma' },
  { token: '{{reference}}', description: 'Hivatkozási szám' },
  { token: '{{userName}}', description: 'Küldő felhasználó neve' },
  { token: '{{userEmail}}', description: 'Küldő felhasználó e-mail címe' },
];

const TemplateVariableHelp = ({ disabled, onInsertVariable, variables, worksheet }) => {
  return (
    <Box
      className="col-span-6"
      sx={{
        mt: 2,
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        backgroundColor: 'grey.50',
      }}
    >
      <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 600 }}>
        Sablonváltozók
      </Typography>
      <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
        A tárgy és a szöveg sablonváltozókat tartalmazhat.
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        Ezek küldéskor automatikusan behelyettesítésre kerülnek a {worksheet ? 'munkalap' : 'bizonylat'} adataival.
      </Typography>
      <Typography variant="subtitle2" sx={{ mt: 3, color: 'text.primary', fontWeight: 600 }}>
        Elérhető változók
      </Typography>
      <Box
        sx={{
          mt: 1.5,
          display: 'grid',
          gap: 1,
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(3, minmax(0, 1fr))',
          },
        }}
      >
        {variables.map((variable) => (
          <ButtonBase
            key={variable.token}
            disabled={disabled}
            onMouseDown={(event) => {
              event.preventDefault();
              onInsertVariable(variable.token);
            }}
            sx={{
              display: 'block',
              width: '100%',
              px: 1.25,
              py: 1,
              textAlign: 'left',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              backgroundColor: 'common.white',
              boxShadow: 1,
              alignItems: 'stretch',
              justifyContent: 'flex-start',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'grey.50',
              },
              '&.Mui-disabled': {
                opacity: 0.6,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                {variable.token}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'right' }}>
                {variable.description}
              </Typography>
            </Box>
          </ButtonBase>
        ))}
      </Box>
    </Box>
  );
};

const TemplateFields = ({ viewField, autoCompleteOptions, forms = [] }) => {
  const { values, errors, setFieldValue, validateField } = useFormikContext();
  const isEmailTemplate = values.templateType === TemplateType.EMAIL;
  const isTextTemplate = values.templateType === TemplateType.TEXT;
  const isSmsTemplate = values.templateType === TemplateType.SMS;
  const isWorksheetEmailTemplate =
    isEmailTemplate &&
    forms.find((form) => Number(form.subMenuId) === Number(values.formId))?.url === 'worksheetReport';
  const bodyInputRef = useRef(null);
  const subjectInputRef = useRef(null);
  const bodyEditorRef = useRef(null);
  const subjectSelectionRef = useRef({ start: 0, end: 0 });
  const [activeField, setActiveField] = useState('body');

  const syncSubjectSelection = () => {
    const input = subjectInputRef.current;

    if (!input) return;

    subjectSelectionRef.current = {
      start: input.selectionStart ?? 0,
      end: input.selectionEnd ?? input.selectionStart ?? 0,
    };
  };

  const insertIntoSubject = (token) => {
    const input = subjectInputRef.current;
    const currentValue = values.subject ?? '';
    const insertion = `${token} `;

    if (!input) {
      setFieldValue('subject', `${currentValue}${insertion}`);
      return;
    }

    const start = subjectSelectionRef.current.start ?? 0;
    const end = subjectSelectionRef.current.end ?? start;
    const nextValue = currentValue.slice(0, start) + insertion + currentValue.slice(end);
    const nextPosition = start + insertion.length;

    setFieldValue('subject', nextValue);
    subjectSelectionRef.current = { start: nextPosition, end: nextPosition };

    requestAnimationFrame(() => {
      input.focus();
      input.setSelectionRange(nextPosition, nextPosition);
    });
  };

  const insertIntoBody = (token) => {
    if (isSmsTemplate) {
      const input = bodyInputRef.current;
      const body = values.body ?? '';
      const start = input?.selectionStart ?? body.length;
      const end = input?.selectionEnd ?? start;
      const insertion = `${token} `;
      setFieldValue('body', body.slice(0, start) + insertion + body.slice(end));
      requestAnimationFrame(() => {
        input?.focus();
        input?.setSelectionRange(start + insertion.length, start + insertion.length);
      });
      return;
    }
    bodyEditorRef.current?.insertText?.(`${token} `);
  };

  const handleInsertVariable = (token) => {
    if (viewField) return;

    if (activeField === 'subject' && isEmailTemplate) {
      insertIntoSubject(token);
      return;
    }

    insertIntoBody(token);
  };

  return (
    <div className="grid grid-cols-6 gap-x-4 mx-4 mb-5">
      <div className="md:col-span-3 col-span-6">
        <Field
          type="text"
          name="name"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Név" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.name} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.name && values.name !== '' }}
        />
      </div>
      <div className="md:col-span-3 col-span-6">
        <Field
          type="autocomplete"
          name="templateType"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Típus" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.templateType} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.templateType !== '' }}
          selectedValue={values.templateType}
          selectedLabelValue={templateTypeOptions.find((item) => item.value === values.templateType)?.label || ''}
          dataset={templateTypeOptions}
          onChange={(_e, newVal) => {
            setFieldValue('templateType', newVal?.value ?? null).then(() => {
              validateField('templateType');
            });
            if (newVal?.value !== TemplateType.EMAIL) {
              setFieldValue('subject', '');
            }
            if (newVal?.value !== TemplateType.TEXT) {
              setFieldValue('documentLevel', null);
            }
          }}
        />
      </div>

      {isTextTemplate && (
        <div className="md:col-span-3 col-span-6">
          <Field
            type="autocomplete"
            name="documentLevel"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text="Bizonylatszint" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.documentLevel} />}
            disabled={viewField}
            InputLabelProps={{ shrink: values.documentLevel !== null }}
            selectedValue={values.documentLevel}
            selectedLabelValue={documentLevelOptions.find((item) => item.value === values.documentLevel)?.label || ''}
            dataset={documentLevelOptions}
            onChange={(_e, newVal) => {
              setFieldValue('documentLevel', newVal?.value ?? null).then(() => {
                validateField('documentLevel');
              });
            }}
          />
        </div>
      )}

      <div className="md:col-span-2 col-span-6">
        <Field
          type="autocomplete"
          name="documentTypeId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Bizonylattípus" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.documentTypeId} />}
          disabled={viewField || isWorksheetEmailTemplate}
          InputLabelProps={{ shrink: values.documentTypeId !== '' && values.documentTypeId !== null }}
          selectedValue={values.documentTypeId}
          selectedLabelValue={
            autoCompleteOptions.documentType.find((item) => item.value === values.documentTypeId)?.label || ''
          }
          dataset={autoCompleteOptions.documentType}
          onChange={(_e, newVal) => {
            setFieldValue('documentTypeId', newVal?.value ?? null).then(() => {
              validateField('documentTypeId');
            });
          }}
        />
      </div>
      <div className="md:col-span-2 col-span-6">
        <Field
          type="autocomplete"
          name="formId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Form" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.formId} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.formId !== '' && values.formId !== null }}
          selectedValue={values.formId}
          selectedLabelValue={autoCompleteOptions.form.find((item) => item.value === values.formId)?.label || ''}
          dataset={autoCompleteOptions.form}
          onChange={(_e, newVal) => {
            setFieldValue('formId', newVal?.value ?? null).then(() => {
              validateField('formId');
            });
            if (forms.find((form) => Number(form.subMenuId) === Number(newVal?.value))?.url === 'worksheetReport') {
              setFieldValue('documentTypeId', null);
            }
          }}
        />
      </div>
      <div className="md:col-span-2 col-span-6">
        <Field
          type="autocomplete"
          name="languageCode"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Nyelv" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.languageCode} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.languageCode !== '' && values.languageCode !== null }}
          selectedValue={values.languageCode}
          selectedLabelValue={
            autoCompleteOptions.language.find((item) => item.value === values.languageCode)?.label || ''
          }
          dataset={autoCompleteOptions.language}
          onChange={(_e, newVal) => {
            setFieldValue('languageCode', newVal?.value ?? null).then(() => {
              validateField('languageCode');
            });
          }}
        />
      </div>

      {isEmailTemplate && (
        <div className="col-span-6">
          <Field
            type="text"
            name="subject"
            as={InputUI}
            fullWidth
            label={<FormLabelUI text="Tárgy" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.subject} />}
            disabled={viewField}
            InputLabelProps={{ shrink: values.subject && values.subject !== '' }}
            inputRef={subjectInputRef}
            onFocus={() => {
              setActiveField('subject');
              syncSubjectSelection();
            }}
            onClick={syncSubjectSelection}
            onKeyUp={syncSubjectSelection}
            onSelect={syncSubjectSelection}
          />
        </div>
      )}

      <div className="col-span-6 mt-2">
        <div className="mb-2">
          <FormLabelUI text="Szöveg" />
        </div>
        {isEmailTemplate ? (
          <RichTextEditorUI
            ref={bodyEditorRef}
            value={values.body ?? ''}
            onChange={(value) => {
              setFieldValue('body', value ?? '');
            }}
            onBlur={() => {
              validateField('body');
            }}
            onFocus={() => {
              setActiveField('body');
            }}
            readOnly={viewField}
          />
        ) : (
          <Field
            type="text"
            name="body"
            as={InputUI}
            fullWidth
            multiline
            rows={12}
            variant="outlined"
            inputRef={bodyInputRef}
            disabled={viewField}
            onBlur={() => validateField('body')}
          />
        )}
        <div className="mt-2">
          <FormErrorUI message={errors.body} />
        </div>
        {false && (
          <Field
            type="text"
            name="body"
            as={InputUI}
            fullWidth
            multiline
            rows={12}
            label={<FormLabelUI text="Szöveg" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.body} />}
            disabled={viewField}
            InputLabelProps={{ shrink: values.body && values.body !== '' }}
          />
        )}
      </div>

      {isEmailTemplate && (
        <TemplateVariableHelp
          disabled={viewField}
          onInsertVariable={handleInsertVariable}
          variables={isWorksheetEmailTemplate ? worksheetEmailTemplateVariables : documentEmailTemplateVariables}
          worksheet={isWorksheetEmailTemplate}
        />
      )}
      {isSmsTemplate && (
        <SmsTemplateVariablePanel disabled={viewField} onInsertVariable={handleInsertVariable} />
      )}
    </div>
  );
};

export default TemplateFields;
